import { ModuleBase, ResponsiveImage, Link, Text, HeadingTag, Carousel } from '@/components';
import classNames from 'classnames';
import React, { useRef, useEffect, useState } from 'react';
import tailwindConfig from '@/tailwind.config';
import { isColorDark } from '@/utils';
import ComponentAnimation from '@/animations/animationComponent/ComponentAnimation';
import { motion } from 'framer-motion';
import Flicking from '@egjs/react-flicking';
import '@egjs/react-flicking/dist/flicking.css';
import CarouselPagination from '@/components/general/CarouselPagination.jsx';

const TwoPortraitMediaAndRte = ({ data }) => {
  const { headingTitle, description, primaryCTA, secondaryCTA } = data || {};
  const [windowWidth, setWindowWidth] = useState(0);
  const scrollRef = useRef(null);
  const { screens } = tailwindConfig.theme;
  const isDark = isColorDark(data.backgroundColour);
  const [containerHeight, setContainerHeight] = useState('auto');
  const flickingRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const willChange = (e) => {
    setCurrentIndex(e.index);
  };

  useEffect(() => {
    const resize = () => {
      setWindowWidth(window.innerWidth);
    };

    resize();

    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  const handleWheel = (e) => {
    const flicking = flickingRef.current;
    if (!flicking || flicking.animating) return;

    const current = flicking.index;
    const lastIndex = flicking.panelCount - 1;

    if (e.deltaY > 0) {
      if (current < lastIndex) {
        e.preventDefault();
        flicking.next();
      }
    } else if (e.deltaY < 0) {
      if (current > 0) {
        e.preventDefault();
        flicking.prev();
      }
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    el?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      el?.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const direction = windowWidth >= parseInt(screens.lg, 10) ? 'y' : 'x';

  const items = data.images.map((image, imageIndex) => (
    <div
      key={imageIndex}
      className="relative aspect-[343/229] w-full shrink-0 md:aspect-[348/233] md:w-[348px] lg:aspect-3/2 lg:w-[500px] xl:aspect-[700/466] xl:w-[700px]"
    >
      <ResponsiveImage
        image={image}
        widths={{ xs: 660, sm: 1152, md: 522, lg: 1050, xl: 1050, xxl: 1050 }}
        heights={{ xs: 653, sm: 1536, md: 350, lg: 699, xl: 699, xxl: 699 }}
        className="overlay pointer-events-none"
      />
    </div>
  ));

  const delayText = data.mediaAlignment === 'Left' ? 0.8 : 0.2;
  const delayPicture = data.mediaAlignment === 'Left' ? 0.2 : 0.8;

  useEffect(() => {
    if (direction === 'y') {
      const count = data.images.length;

      if (count > 5 && count <= 10) {
        setContainerHeight(count * 52);
      } else if (count > 3 && count <= 5) {
        setContainerHeight(250);
      } else if (count <= 3) {
        setContainerHeight(200);
      }
    }
  }, [windowWidth, data.images.length]);

  return (
    <ModuleBase data={data} className="">
      <motion.div
        ref={scrollRef}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ type: 'tween', duration: 0.8 }}
        className={classNames('relative w-full')}
        style={
          windowWidth && data.images.length > 1 && direction === 'y' ? { height: `${containerHeight}vh` } : undefined
        }
      >
        <div
          className={classNames(
            'main-data container flex flex-col-reverse gap-y-7 pb-0 md:justify-between',
            data.mediaAlignment === 'Left' ? 'md:flex-row-reverse' : 'md:flex-row',
            data.images.length > 1 ? 'lg:sticky lg:top-1/3 xl:top-[24.3%]' : '',
          )}
        >
          <ComponentAnimation
            delay={delayText}
            className={classNames('text-wrapper md:w-[348px] lg:my-auto xl:w-[496px] xxl:w-[562px]')}
          >
            <div className="title-wrapper" role="contentinfo" aria-label="contentinfo">
              {headingTitle && (
                <HeadingTag animate data={headingTitle} className="color-from-bg title-05 font-kepler" />
              )}
            </div>
            {(description || primaryCTA || secondaryCTA) && (
              <div className="mt-4 md:mt-[18px] lg:mt-[23px]">
                <Text
                  animate
                  dangerouslySetInnerHTML={{ __html: description }}
                  className="color-from-bg body-03 !text-opacity-75"
                />
                {(primaryCTA || secondaryCTA) && (
                  <div className="mt-05 flex flex-col gap-2 md:flex-row">
                    <Link
                      animate
                      className={classNames('btn primary', isDark ? 'dark' : 'light')}
                      link={data.primaryCTA}
                    />
                    <Link
                      animate
                      className={classNames('btn secondary', isDark ? 'dark' : 'light')}
                      link={data.secondaryCTA}
                    />
                  </div>
                )}
              </div>
            )}
          </ComponentAnimation>

          <ComponentAnimation delay={delayPicture}>
            <div className="relative my-auto overflow-hidden md:h-[233px] md:w-[348px] lg:h-[333px] lg:w-[500px] xl:h-[466px] xl:w-[700px]">
              {data.images.length > 1 ? (
                <>
                  <div className="relative my-auto hidden overflow-hidden md:h-[233px] md:w-[348px] lg:flex lg:h-[333px] lg:w-[500px] xl:h-[466px] xl:w-[700px]">
                    <Flicking
                      ref={flickingRef}
                      horizontal={false}
                      onWillChange={willChange}
                      className="flex h-full flex-col"
                    >
                      {data.images.map((image, imageIndex) => (
                        <div key={imageIndex} className="relative h-full w-full shrink-0">
                          <ResponsiveImage
                            image={image}
                            widths={{ xs: 660, sm: 1152, md: 522, lg: 1050, xl: 1050, xxl: 1050 }}
                            heights={{ xs: 653, sm: 1536, md: 350, lg: 699, xl: 699, xxl: 699 }}
                            className="overlay pointer-events-none h-full w-full object-cover"
                          />
                        </div>
                      ))}
                    </Flicking>
                    <CarouselPagination
                      current={currentIndex}
                      total={data.images.length}
                      className={`absolute right-6 top-1/2 z-20 hidden -translate-y-1/2 lg:flex`}
                    />
                  </div>

                  {direction === 'x' && (
                    <Carousel
                      items={items}
                      moduleId={data.moduleId}
                      animationStyle="elegant"
                      showPagination
                      paginationType="slider"
                      className="[&_.carousel-pagination-slider-track]:w-[81px] [&_.carousel-pagination]:absolute [&_.carousel-pagination]:bottom-4 [&_.carousel-pagination]:left-1/2 [&_.carousel-pagination]:-translate-x-1/2"
                    />
                  )}
                </>
              ) : (
                <div className="relative aspect-[343/229] w-full md:aspect-[348/233] md:w-[348px] lg:aspect-3/2 lg:w-[500px] xl:aspect-[700/466] xl:w-[700px]">
                  <ResponsiveImage
                    image={data.images[0]}
                    widths={{ xs: 660, sm: 1152, md: 522, lg: 1050, xl: 1050, xxl: 1050 }}
                    heights={{ xs: 653, sm: 1536, md: 350, lg: 699, xl: 699, xxl: 699 }}
                    className="overlay pointer-events-none"
                  />
                </div>
              )}
            </div>
          </ComponentAnimation>
        </div>
      </motion.div>
    </ModuleBase>
  );
};

export default TwoPortraitMediaAndRte;
